import { Lock } from "lucide-react"
import { Navigate, useNavigate } from "react-router-dom"
import { AppHeader } from "@/components/dashboard/AppHeader"
import { INVESTMENT_STRATEGIES } from "@/components/activation/data/strategies"
import { useActivation } from "@/lib/activation-context"
import { useSession } from "@/lib/session-context"
import { cn } from "@/lib/utils"

const METRIC_LABELS = {
  roce: "ROCE",
  roc: "Profit on Cost",
  profit: "Profit on GDV",
  cashflow: "Monthly Cashflow",
}

function metricSummary(metric, threshold) {
  if (!metric || !threshold) return ""
  const unit = metric === "cashflow" ? "£" : "%"
  return `${METRIC_LABELS[metric] ?? metric} ≥ ${threshold}${unit}`
}

function strategyTitle(id) {
  return INVESTMENT_STRATEGIES.find((s) => s.id === id)?.title ?? ""
}

function buildSections({ step1, step2, step3, step4, step5, workflowVersion }) {
  const targetSummary =
    step1.targetMonthlyCashflowGross && step1.timeframeYears
      ? `£${step1.targetMonthlyCashflowGross} gross cashflow over ${step1.timeframeYears} years`
      : ""

  const primary = strategyTitle(step2.primaryStrategy)
  const secondary = strategyTitle(step2.secondaryStrategy)
  const strategySummary = primary ? (secondary ? `${primary} + ${secondary}` : primary) : ""

  const areaSummary = step3.selectedPostcodes.length ? step3.selectedPostcodes.join(", ") : ""

  const currentWorkflow = Number(workflowVersion) >= 2
  const dynamicsCount = Object.keys(step4.marketData ?? {}).length
  const dynamicsSummary = dynamicsCount
    ? `Market baselines configured for ${dynamicsCount} postcode${dynamicsCount === 1 ? "" : "s"}`
    : ""

  const criteria = currentWorkflow ? step4 : step5
  const primaryCriteria = metricSummary(criteria.primaryMetric, criteria.primaryThreshold)
  const secondaryCriteria = metricSummary(criteria.secondaryMetric, criteria.secondaryThreshold)
  const criteriaSummary = primaryCriteria
    ? secondaryCriteria
      ? `${primaryCriteria} + ${secondaryCriteria}`
      : primaryCriteria
    : ""

  const common = [
    {
      id: 1,
      title: "Set Investment Target",
      description: "Define your financial targets to configure the execution engine.",
      summary: targetSummary,
    },
    {
      id: 2,
      title: "Select Strategy",
      description: "Choose your primary investment model to define how you will generate returns.",
      summary: strategySummary,
    },
    {
      id: 3,
      title: "Select Goldmine Territory",
      description: "Choose the acquisition territory Sentinel recommends for your declared sourcing strategy.",
      summary: areaSummary,
    },
  ]

  if (currentWorkflow) {
    return [...common, {
      id: 4,
      title: "Define Deal Criteria",
      description: "Set the minimum thresholds a deal must meet before you pursue or offer.",
      summary: criteriaSummary,
    }]
  }

  return [...common,
    {
      id: 4,
      title: "Capture Area Dynamics",
      description: "Scan each selected postcode area and build editable market baselines.",
      summary: dynamicsSummary,
    },
    {
      id: 5,
      title: "Define Deal Criteria",
      description: "Set the minimum thresholds a deal must meet before you pursue or offer.",
      summary: criteriaSummary,
    },
  ]
}

export function SettingsScreen() {
  const { currentUser } = useSession()
  const activation = useActivation()
  const navigate = useNavigate()

  if (!currentUser) return <Navigate to="/" replace />

  const sections = buildSections(activation)
  const activationCompleted = Boolean(activation.complete)

  return (
    <main className="min-h-screen bg-background text-foreground">
      <AppHeader status="online" showNav />

      <section className="mx-auto flex w-full max-w-[800px] flex-col p-4 duration-500 animate-in fade-in slide-in-from-bottom-4 md:p-8 lg:p-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Settings</h1>

          <div className="mt-4 flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <span className="text-base font-semibold text-foreground">Account Settings</span>
            <button
              type="button"
              onClick={() => navigate("/settings/account")}
              aria-label="View Account Settings"
              className="w-full rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 sm:w-auto"
            >
              View
            </button>
          </div>

          <p className="mt-4 text-lg text-muted-foreground">Review and edit your investment setup</p>
        </div>

        <div className="mb-8">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
              Investment Setup
            </h2>
            {activationCompleted ? null : (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Lock className="h-3 w-3" />
                Locked
              </span>
            )}
          </div>

          {activationCompleted ? null : (
            <p className="mb-4 text-sm text-muted-foreground">
              Complete activation to unlock investment setup edits.
            </p>
          )}

          <div className={cn("flex flex-col gap-4", activationCompleted ? "" : "opacity-60")}>
            {sections.map((section) => (
              <SectionCard
                key={section.id}
                section={section}
                locked={!activationCompleted}
                onEdit={() => navigate(`/activation/step/${section.id}`)}
              />
            ))}
          </div>
        </div>
      </section>
    </main>
  )
}

function SectionCard({ section, locked, onEdit }) {
  const configured = section.summary.length > 0

  return (
    <div className="group flex flex-col justify-between gap-4 rounded-xl border border-border bg-card p-6 shadow-sm transition-all hover:border-primary/20 sm:flex-row sm:items-center">
      <div className="flex-1">
        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
          {section.title}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">{section.description}</p>

        <div className="mt-3">
          {configured ? (
            <span className="inline-flex items-center rounded-lg border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary">
              {section.summary}
            </span>
          ) : (
            <span className="inline-flex items-center rounded-lg border border-border bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground">
              Not configured
            </span>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={onEdit}
        disabled={locked}
        aria-label={locked ? `${section.title} locked` : `Edit ${section.title}`}
        className={cn(
          "inline-flex w-full items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold shadow-sm transition-colors sm:w-auto",
          locked
            ? "cursor-not-allowed border border-border bg-muted text-muted-foreground"
            : "bg-primary text-primary-foreground hover:bg-primary/90",
        )}
      >
        {locked ? <Lock className="h-3.5 w-3.5" /> : null}
        {locked ? "Locked" : "Edit"}
      </button>
    </div>
  )
}
